import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Logo, ArrowRight } from '../Login/svg';
import { Card } from '../../components/UI/Card';
import { Input } from '../../components/UI/Input';
import { MaskedInput } from '../../components/UI/MaskedInput';
import { Button } from '../../components/UI/Button';
import { Loader } from '../../components/UI/Loader';
import SuccessFeedbackIcon from '../../components/UI/SuccessFeedbackIcon';
import { SYSTEM_TYPE } from '../../enums/request';
import AuthService from '../../services/AuthService';

import {
  ActionButtons,
  Content,
  Description,
  DocumentInputWrapper,
  ErrorMessage,
  ForgotPasswordContent,
  HeaderContent,
  SuccessActions,
  SuccessContainer,
  SuccessContent,
  SuccessMessage,
  Title,
} from './styles';

const ArrowLeft: React.FC = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M6.34502 8.46741L10.59 4.22991C10.6597 4.15961 10.7427 4.10381 10.8341 4.06574C10.9255 4.02766 11.0235 4.00806 11.1225 4.00806C11.2215 4.00806 11.3196 4.02766 11.411 4.06574C11.5023 4.10381 11.5853 4.15961 11.655 4.22991C11.7947 4.37043 11.8731 4.56052 11.8731 4.75866C11.8731 4.95679 11.7947 5.14688 11.655 5.28741L7.94252 9.03741L11.655 12.7499C11.7947 12.8904 11.8731 13.0805 11.8731 13.2787C11.8731 13.4768 11.7947 13.6669 11.655 13.8074C11.5856 13.8783 11.5027 13.9346 11.4113 13.9733C11.3199 14.0119 11.2217 14.032 11.1225 14.0324C11.0233 14.032 10.9251 14.0119 10.8337 13.9733C10.7423 13.9346 10.6595 13.8783 10.59 13.8074L6.34502 9.56991C6.26881 9.49967 6.20806 9.41444 6.16653 9.31956C6.12499 9.22469 6.10352 9.12223 6.10352 9.01866C6.10352 8.91508 6.12499 8.81263 6.16653 8.71775C6.20806 8.62288 6.26881 8.53764 6.34502 8.46741Z"
        fill="#101010"
      />
    </svg>
  );
};

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [document, setDocument] = useState('');
  const [isRequesting, setIsRequesting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const clearState = () => {
    setErrorMessage('');
    setSuccess(false);
  };

  const handleEmailChange = (target: any) => {
    clearState();
    setEmail(target.value);
  };

  const handleDocumentChange = (target: any) => {
    clearState();
    setDocument(target.value);
  };

  const isEmailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email), [email]);
  const documentWithoutMask = useMemo(() => document.replace(/\D/g, ''), [document]);
  const isDocumentValid = useMemo(() => documentWithoutMask.length === 11, [documentWithoutMask]);

  const isButtonDisabled = !isEmailValid || !isDocumentValid || isRequesting;

  const handleSubmit = async () => {
    try {
      setIsRequesting(true);
      setErrorMessage('');
      setSuccess(false);

      await AuthService.postResetPassword({
        email,
        document: documentWithoutMask,
        systemType: SYSTEM_TYPE.BARICONSIG,
      });

      setSuccess(true);
    } catch (error: any) {
      const message = error?.response?.data?.messages?.[0];

      if (message === 'User is not found by email') {
        setErrorMessage('E-mail inválido. Tente novamente.');
      } else if (message === 'User is not found by document') {
        setErrorMessage('CPF inválido. Tente novamente.');
      } else {
        setErrorMessage('Não foi possível enviar a solicitação. Tente novamente.');
      }
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <ForgotPasswordContent>
      <Card>
        <Content>
          {!success ? <Logo /> : null}
          {!success ? (
            <HeaderContent>
              <Title>Esqueci minha senha</Title>
              <Description>Para redefinir sua senha, digite seu e-mail e CPF.</Description>
            </HeaderContent>
          ) : null}

          {success ? (
            <SuccessContainer>
              <SuccessContent>
                <SuccessFeedbackIcon />
                <SuccessMessage>Enviamos um e-mail com o link para redefinição de senha.</SuccessMessage>
              </SuccessContent>
              <SuccessActions>
                <Button white onClick={() => navigate('/')}>
                  Ok, entendi
                </Button>
              </SuccessActions>
            </SuccessContainer>
          ) : (
            <form>
              {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
              <Input
                id="email"
                name="email"
                label="E-mail"
                type="email"
                onChange={handleEmailChange}
                disabled={isRequesting}
                placeholder="Ex. email@email.com.br"
              />
              <DocumentInputWrapper>
                <MaskedInput
                  id="document"
                  name="document"
                  label="CPF"
                  type="text"
                  mask="999.999.999-99"
                  value={document}
                  onChange={handleDocumentChange}
                  isDisabled={isRequesting}
                  placeholder="Digite seu CPF"
                />
              </DocumentInputWrapper>
              <ActionButtons>
                <Button transparent fixedWidth iconLeft={ArrowLeft} onClick={() => navigate('/')}>
                  Cancelar
                </Button>
                <Button disabled={isButtonDisabled} black fixedWidth onClick={handleSubmit}>
                  {isRequesting ? 'Enviando...' : 'Enviar'}
                  {isRequesting ? <Loader /> : <ArrowRight />}
                </Button>
              </ActionButtons>
            </form>
          )}
        </Content>
      </Card>
    </ForgotPasswordContent>
  );
};

export default ForgotPassword;
